import { useMemo } from "react";
import { usePaymentPlanByIri } from "src/api/client-api/payment/usePaymentPlanByIri";
import type { PaymentPlan } from "src/api/client-api/payment/usePaymentPlans";
import type { CompanySubscription } from "src/api/client-api/payment/useCurrentSubscription";

const isPaymentPlanObject = (value: unknown): value is PaymentPlan =>
    !!value &&
    typeof value === "object" &&
    "id" in (value as Record<string, unknown>) &&
    "@id" in (value as Record<string, unknown>);

export const useResolvedPaymentPlan = (
    subscription: CompanySubscription | undefined,
    plans: PaymentPlan[] | undefined,
) => {
    const paymentPlanValue = subscription?.paymentPlan;

    const embeddedPlan = useMemo(
        () => (isPaymentPlanObject(paymentPlanValue) ? paymentPlanValue : undefined),
        [paymentPlanValue],
    );

    const planIri = useMemo(() => {
        if (typeof paymentPlanValue !== "string") return undefined;

        if (paymentPlanValue.includes("/api/payment/payment_plans/") || paymentPlanValue.includes("/payment_plans/")) {
            return paymentPlanValue;
        }

        return undefined;
    }, [paymentPlanValue]);

    const { data: fetchedPlan } = usePaymentPlanByIri(planIri);

    const resolvedPlan = useMemo(() => {
        const iriForMarking = planIri || embeddedPlan?.["@id"];
        const resolvedPlanFromList = (plans || []).find(
            (p) => p["@id"] === iriForMarking || (embeddedPlan?.id && p.id === embeddedPlan.id),
        );

        return resolvedPlanFromList ?? embeddedPlan ?? fetchedPlan ?? undefined;
    }, [planIri, embeddedPlan, plans, fetchedPlan]);

    return { resolvedPlan };
};
